import React, { useState } from 'react';
import { toast } from 'sonner';
import { getObjectId, Order, Vehicle } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { getVehiclePlateNumber } from '@hero/core';
import { VehicleMainIcon, PlateIcon, CalendarIcon, EyeIcon, PencilIcon } from '@hero/web-icons';
import { VehicleName } from '../../../Vehicle/VehicleName';
import { Collapse } from '@hero/web-components';
import { DetailRow } from './DetailRow';
import { useVehicleOptions, useVehicleDrawerStore } from '@hero/hooks-shared';
import { useAssignOrderApi } from '@hero/api-hooks';
import { OrderVehiclePickerDrawer } from '../OrderVehiclePickerDrawer';

export interface VehicleDetailsProps {
  order: Order;
}

export const VehicleDetails: React.FC<VehicleDetailsProps> = ({ order }) => {
  const { t } = useTranslation('m-order');
  const { getVehicleDisplayName } = useVehicleOptions();
  const openVehicleDrawer = useVehicleDrawerStore(s => s.open);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Vehicle-only assignment goes through the same Phase-6 single-PATCH
  // pipeline that the driver picker uses; we just feed `vehicle` (no
  // `driver`) so the backend keeps the existing driver in place. Cache
  // invalidation flows through `useAssignOrderApi` automatically.
  const { mutate: assignVehicle, isPending: isAssigning } = useAssignOrderApi({
    onSuccess: () => {
      toast.success(t('drawer.driver_picker.saved_toast', { defaultValue: 'Vehicle updated' }));
      setIsPickerOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleVehiclePick = (vehicle: Vehicle) => {
    assignVehicle({
      _id: order._id,
      vehicle: { operation: 'add', _id: getObjectId(String(vehicle._id)) },
    });
  };

  const vehicle = order.vehicle;
  const hasVehicle = !!vehicle;
  const vehicleType = order.load.vehicles[0] || '';
  const plateNumber = vehicle?.registration?.plate;
  const plateDisplay =
    hasVehicle && plateNumber ? getVehiclePlateNumber(plateNumber).displayAll : '';
  const vehicleDescription = hasVehicle
    ? `${getVehicleDisplayName(vehicleType)}${plateDisplay ? ` • ${plateDisplay}` : ''}`
    : t('vehicle_details.no_vehicle_assigned');

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={t('vehicle_details.title')}
        description={vehicleDescription}
        icon={
          <div className="p-1.5 bg-purple-50 rounded-lg">
            <VehicleMainIcon className="size-5 text-purple-600" />
          </div>
        }
        action={
          <div className="flex items-center gap-1.5">
            {hasVehicle && vehicle?._id && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  openVehicleDrawer(String(vehicle._id));
                }}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label={t('vehicle_details.view_vehicle')}>
                <EyeIcon className="w-3.5 h-3.5 text-gray-600" />
              </button>
            )}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                setIsPickerOpen(true);
              }}
              disabled={isAssigning}
              className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
              aria-label={
                hasVehicle ? t('vehicle_details.change_vehicle') : t('vehicle_details.pick_vehicle')
              }>
              <PencilIcon className="w-3.5 h-3.5 text-gray-600" />
            </button>
          </div>
        }
        defaultOpen={false}
        disabled={!hasVehicle}>
        {hasVehicle && (
          <div className="space-y-0">
            {plateNumber && (
              <DetailRow
                icon={<PlateIcon className="size-5 text-gray-900" />}
                label={t('vehicle_details.plate')}
                value={getVehiclePlateNumber(plateNumber).displayAll}
              />
            )}

            <DetailRow
              icon={<VehicleMainIcon className="size-5 text-gray-900" />}
              label={t('vehicle_details.vehicle_type')}
              value={
                <VehicleName
                  vehicle={vehicleType}
                  className="text-gray-900 text-[14px] font-[500] leading-[22px]"
                />
              }
            />

            {vehicle.classification?.year && (
              <DetailRow
                icon={<CalendarIcon className="size-5 text-gray-900" />}
                label={t('vehicle_details.year')}
                value={vehicle.classification.year.toString()}
                showDivider={false}
              />
            )}
          </div>
        )}
      </Collapse>

      <OrderVehiclePickerDrawer
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onPick={handleVehiclePick}
      />
    </div>
  );
};
